import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { getCountryConfig } from '@/config/countries/registry'
import type {
  CalculatorState,
  UserProfile,
  LocationPreferences,
  IncomeExpenses,
  IndiaAssets,
  ResidentCountryAssets,
  ArchivedAssets,
  SafetyNet,
  Liabilities,
  SavingsConfig,
  Assumptions,
  WizardState,
  WizardStep,
  FIREResults,
  WorkDurationAnalysis,
  YearlyProjection,
  MonteCarloAnalysis,
  TaxAnalysis,
  Recommendation,
  ComplianceItem,
  RNORQuickResult,
  Goal,
  Child,
  Liability,
  OtherAsset,
  CountryCode,
  RSUGrant,
  StockOption,
} from '@/types'
import { defaults } from '@/data'

// ==========================================
// Default Values (aligned with defaults.json)
// ==========================================

const DEFAULT_EXCHANGE_RATE = defaults.profile.exchangeRate

const defaultProfile: UserProfile = {
  firstName: '',
  currentAge: defaults.profile.currentAge,
  retirementAge: defaults.profile.retirementAge,
  lifeExpectancy: defaults.profile.lifeExpectancy,
  maritalStatus: 'single',
  spouseWorking: false,
  dependentParents: 0,
  children: [],
  residentCountry: 'USA',
  targetCountry: 'India',
  exchangeRate: DEFAULT_EXCHANGE_RATE,
  useCustomExchangeRate: false,
  yearsWorkedInUSA: undefined,  // For Social Security eligibility
}


const defaultLocation: LocationPreferences = {
  primaryCityId: defaults.location.primaryCityId,
  comparisonCityIds: [],
  lifestyle: defaults.location.lifestyle,
  lifestyleMultiplier: 1.0,
  housingOwnership: 'rental',
  housingType: '3BHK',
  customMiscExpense: 0,
  includeSchoolFees: false,
  schoolBoard: 'cbse', // Default to CBSE (most common board)
}

const defaultIncomeExpenses: IncomeExpenses = {
  annualIncomeUSD: defaults.incomeExpenses.annualIncomeUSD,
  annualRSUIncomeUSD: 0,
  annualIncomeGrowthPercent: 3, // Default 3% annual income growth
  monthlyExpensesUSD: defaults.incomeExpenses.monthlyExpensesUSD,
  expectedExpenseReductionPercent: defaults.incomeExpenses.expectedExpenseReductionPercent,
  // India phase defaults - undefined means auto-estimate
  expectedIndiaSalaryINR: undefined,
  expectedIndiaSavingsRatePercent: undefined,
}

const defaultIndiaAssets: IndiaAssets = {
  npsBalance: 0,
  epfBalance: 0,
  ppfBalance: 0,
  mutualFunds: 0,
  stocks: 0,
  fixedDeposits: 0,
  gold: 0,
  realEstateValue: 0,
  realEstateRentalIncome: 0,
  nreAccountBalance: 0,
  nroAccountBalance: 0,
  otherAssets: [],
}

const defaultResidentCountryAssets: ResidentCountryAssets = {
  countryCode: 'USA',
  assets: {}, // Empty initially, will be populated based on country config
  complexAssets: {
    rsuGrants: [],
    stockOptions: [],
    otherAssets: [],
  },
  settings: {
    stateTaxTier: 'none',
  },
  lastUpdated: new Date(),
}

const defaultLiabilities: Liabilities = {
  USA: [],
  UAE: [],
  India: [],
}

const defaultSafetyNet: SafetyNet = {
  termInsuranceCoverINR: 0,
  healthInsuranceCoverINR: 0,
  medicalBufferINR: 0,
  emergencyFundMonths: defaults.safetyNet.emergencyFundMonths,
  currentEmergencyFundUSD: 0,
  currentEmergencyFundINR: 0,
}

const defaultSavings: SavingsConfig = {
  monthlySIPUSD: 0,
  monthlySIPINR: 0,
  annualStepUpPercent: defaults.savings.annualStepUpPercent,
  goals: [],
  riskTolerance: 'moderate',
  investmentExperience: 'intermediate',
}

const defaultAssumptions: Assumptions = {
  generalInflation: defaults.assumptions.inflationIndia,
  medicalInflation: defaults.assumptions.medicalInflation,
  educationInflation: defaults.assumptions.educationInflation,
  preRetirementReturn: defaults.assumptions.expectedReturnIndia,
  postRetirementReturn: defaults.assumptions.fdReturnIndia,
  safeWithdrawalRate: defaults.assumptions.safeWithdrawalRate,
  taxRegime: 'new',
  expectedPensionAnnual: 0,
  expectedRentalMonthly: 0,
  currencyDepreciation: defaults.assumptions.currencyDepreciation,
}

const defaultWizardState: WizardState = {
  currentStep: 1,
  completedSteps: [],
  isCalculating: false,
  hasUnsavedChanges: false,
}

// ==========================================
// Store Actions Interface
// ==========================================

interface AppStoreActions {
  // Profile actions
  updateProfile: (profile: Partial<UserProfile>) => void
  addChild: (child: Child) => void
  updateChild: (childId: string, child: Partial<Child>) => void
  removeChild: (childId: string) => void

  // Location actions
  updateLocation: (location: Partial<LocationPreferences>) => void

  // Income & Expenses actions
  updateIncomeExpenses: (data: Partial<IncomeExpenses>) => void

  // Resident Country Assets (country-agnostic - works for USA, UAE, etc.)
  setResidentCountry: (code: CountryCode) => void
  updateResidentAsset: (fieldMapping: string, value: number) => void
  updateResidentAssets: (assets: Record<string, number>) => void
  updateResidentComplexAssets: (complexAssets: Partial<NonNullable<ResidentCountryAssets['complexAssets']>>) => void
  updateResidentSettings: (settings: Partial<NonNullable<ResidentCountryAssets['settings']>>) => void
  addResidentOtherAsset: (asset: OtherAsset) => void
  removeResidentOtherAsset: (assetId: string) => void
  resetResidentAssets: () => void
  restoreArchivedAssets: (code: CountryCode) => void

  // India Assets (destination country)
  updateIndiaAssets: (assets: Partial<IndiaAssets>) => void
  addIndiaOtherAsset: (asset: OtherAsset) => void
  removeIndiaOtherAsset: (assetId: string) => void
  
  // Liabilities actions
  updateLiabilities: (liabilities: Partial<Liabilities>) => void
  addLiability: (liability: Liability) => void
  updateLiability: (liabilityId: string, liability: Partial<Liability>) => void
  removeLiability: (liabilityId: string) => void
  
  // Safety Net actions
  updateSafetyNet: (safetyNet: Partial<SafetyNet>) => void
  
  // Savings & Goals actions
  updateSavings: (savings: Partial<SavingsConfig>) => void
  addGoal: (goal: Goal) => void
  updateGoal: (goalId: string, goal: Partial<Goal>) => void
  removeGoal: (goalId: string) => void
  
  // Assumptions actions
  updateAssumptions: (assumptions: Partial<Assumptions>) => void
  
  // Wizard actions
  setCurrentStep: (step: WizardStep) => void
  markStepCompleted: (step: WizardStep) => void
  goToNextStep: () => void
  goToPreviousStep: () => void
  setCalculating: (isCalculating: boolean) => void
  setHasUnsavedChanges: (hasChanges: boolean) => void
  
  // Results actions
  setFIREResults: (results: FIREResults) => void
  setWorkDurationAnalysis: (analysis: WorkDurationAnalysis) => void
  setProjections: (projections: YearlyProjection[]) => void
  setMonteCarloAnalysis: (analysis: MonteCarloAnalysis) => void
  setTaxAnalysis: (analysis: TaxAnalysis) => void
  setRecommendations: (recommendations: Recommendation[]) => void
  setComplianceItems: (items: ComplianceItem[]) => void
  setRNORResult: (result: RNORQuickResult) => void
  
  // Utility actions
  resetToDefaults: () => void
  exportData: () => string
  importData: (jsonData: string) => boolean
  updateLastSaved: () => void

  // Dev helpers (only use in development)
  loadTestData: () => void
}

// ==========================================
// Store Type
// ==========================================

type AppStore = CalculatorState & AppStoreActions

// ==========================================
// Create Store
// ==========================================

const getDefaultState = (): CalculatorState => ({
  profile: { ...defaultProfile },
  location: { ...defaultLocation },
  incomeExpenses: { ...defaultIncomeExpenses },
  residentCountryAssets: { ...defaultResidentCountryAssets },
  archivedAssets: [],
  indiaAssets: { ...defaultIndiaAssets },
  liabilities: { ...defaultLiabilities },
  safetyNet: { ...defaultSafetyNet },
  savings: { ...defaultSavings },
  assumptions: { ...defaultAssumptions },
  wizardState: { ...defaultWizardState },
})

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...getDefaultState(),

      // ==========================================
      // Profile Actions
      // ==========================================
      updateProfile: (profile) =>
        set((state) => ({
          profile: { ...state.profile, ...profile },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      addChild: (child) =>
        set((state) => ({
          profile: {
            ...state.profile,
            children: [...state.profile.children, child],
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      updateChild: (childId, childUpdate) =>
        set((state) => ({
          profile: {
            ...state.profile,
            children: state.profile.children.map((child) =>
              child.id === childId ? { ...child, ...childUpdate } : child
            ),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      removeChild: (childId) =>
        set((state) => ({
          profile: {
            ...state.profile,
            children: state.profile.children.filter((child) => child.id !== childId),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // Location Actions
      // ==========================================
      updateLocation: (location) =>
        set((state) => ({
          location: { ...state.location, ...location },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // Income & Expenses Actions
      // ==========================================
      updateIncomeExpenses: (data) =>
        set((state) => {
          const updated = { ...state.incomeExpenses, ...data }
          // Calculate savings rate (include RSU income)
          const totalIncome = updated.annualIncomeUSD + (updated.annualRSUIncomeUSD || 0)
          const annualExpenses = updated.monthlyExpensesUSD * 12
          const annualSavings = totalIncome - annualExpenses
          updated.monthlySavingsUSD = annualSavings / 12
          updated.savingsRatePercent = totalIncome > 0 ? (annualSavings / totalIncome) * 100 : 0

          return {
            incomeExpenses: updated,
            wizardState: { ...state.wizardState, hasUnsavedChanges: true },
          }
        }),

      // ==========================================
      // Resident Country Asset Actions (Country-Agnostic)
      // ==========================================
      setResidentCountry: (code) =>
        set((state) => {
          // Archive ALL current assets before switching (including complexAssets and settings)
          const archived: ArchivedAssets = {
            countryCode: state.residentCountryAssets.countryCode,
            assets: { ...state.residentCountryAssets.assets },
            complexAssets: state.residentCountryAssets.complexAssets
              ? { ...state.residentCountryAssets.complexAssets }
              : undefined,
            settings: state.residentCountryAssets.settings
              ? { ...state.residentCountryAssets.settings }
              : undefined,
            archivedAt: new Date(),
          }

          // Check if we have archived assets for the new country
          const existingArchive = state.archivedAssets.find((a) => a.countryCode === code)

          return {
            residentCountryAssets: existingArchive
              ? {
                  countryCode: code,
                  assets: { ...existingArchive.assets },
                  // Restore complexAssets and settings from archive
                  complexAssets: existingArchive.complexAssets
                    ? { ...existingArchive.complexAssets }
                    : { rsuGrants: [], stockOptions: [], otherAssets: [] },
                  settings: existingArchive.settings
                    ? { ...existingArchive.settings }
                    : {},
                  lastUpdated: new Date(),
                }
              : {
                  countryCode: code,
                  assets: {},
                  complexAssets: { rsuGrants: [], stockOptions: [], otherAssets: [] },
                  settings: {},
                  lastUpdated: new Date(),
                },
            archivedAssets: [
              ...state.archivedAssets.filter((a) => a.countryCode !== code),
              archived,
            ],
            profile: {
              ...state.profile,
              residentCountry: code,
            },
            wizardState: { ...state.wizardState, hasUnsavedChanges: true },
          }
        }),

      updateResidentAsset: (fieldMapping, value) =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            assets: {
              ...state.residentCountryAssets.assets,
              [fieldMapping]: value,
            },
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      updateResidentAssets: (assets) =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            assets: {
              ...state.residentCountryAssets.assets,
              ...assets,
            },
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      resetResidentAssets: () =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            assets: {},
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      restoreArchivedAssets: (code) =>
        set((state) => {
          const archived = state.archivedAssets.find((a) => a.countryCode === code)
          if (!archived) return state

          return {
            residentCountryAssets: {
              countryCode: code,
              assets: { ...archived.assets },
              lastUpdated: new Date(),
            },
            archivedAssets: state.archivedAssets.filter((a) => a.countryCode !== code),
            profile: {
              ...state.profile,
              residentCountry: code,
            },
            wizardState: { ...state.wizardState, hasUnsavedChanges: true },
          }
        }),

      updateResidentComplexAssets: (complexAssets) =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            complexAssets: {
              ...state.residentCountryAssets.complexAssets,
              ...complexAssets,
            },
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      updateResidentSettings: (settings) =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            settings: {
              ...state.residentCountryAssets.settings,
              ...settings,
            },
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      addResidentOtherAsset: (asset) =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            complexAssets: {
              ...state.residentCountryAssets.complexAssets,
              otherAssets: [
                ...(state.residentCountryAssets.complexAssets?.otherAssets || []),
                asset,
              ],
            },
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      removeResidentOtherAsset: (assetId) =>
        set((state) => ({
          residentCountryAssets: {
            ...state.residentCountryAssets,
            complexAssets: {
              ...state.residentCountryAssets.complexAssets,
              otherAssets: (state.residentCountryAssets.complexAssets?.otherAssets || [])
                .filter((a) => a.id !== assetId),
            },
            lastUpdated: new Date(),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // India Assets (Destination Country)
      // ==========================================
      updateIndiaAssets: (assets) =>
        set((state) => ({
          indiaAssets: { ...state.indiaAssets, ...assets },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      addIndiaOtherAsset: (asset) =>
        set((state) => ({
          indiaAssets: {
            ...state.indiaAssets,
            otherAssets: [...state.indiaAssets.otherAssets, asset],
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      removeIndiaOtherAsset: (assetId) =>
        set((state) => ({
          indiaAssets: {
            ...state.indiaAssets,
            otherAssets: state.indiaAssets.otherAssets.filter((a) => a.id !== assetId),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // Liabilities Actions
      // ==========================================
      updateLiabilities: (liabilities) =>
        set((state) => ({
          liabilities: { ...state.liabilities, ...liabilities },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      addLiability: (liability) =>
        set((state) => {
          const countryKey = liability.country
          const currentLiabilities = state.liabilities[countryKey] || []
          return {
            liabilities: {
              ...state.liabilities,
              [countryKey]: [...currentLiabilities, liability],
            },
            wizardState: { ...state.wizardState, hasUnsavedChanges: true },
          }
        }),

      updateLiability: (liabilityId, liabilityUpdate) =>
        set((state) => {
          const newLiabilities = { ...state.liabilities }
          // Iterate over all countries to find and update the liability
          Object.keys(newLiabilities).forEach((countryCode) => {
            const key = countryCode as keyof typeof newLiabilities
            newLiabilities[key] = newLiabilities[key].map((l) =>
              l.id === liabilityId ? { ...l, ...liabilityUpdate } : l
            )
          })
          return {
            liabilities: newLiabilities,
            wizardState: { ...state.wizardState, hasUnsavedChanges: true },
          }
        }),

      removeLiability: (liabilityId) =>
        set((state) => {
          const newLiabilities = { ...state.liabilities }
          // Iterate over all countries to find and remove the liability
          Object.keys(newLiabilities).forEach((countryCode) => {
            const key = countryCode as keyof typeof newLiabilities
            newLiabilities[key] = newLiabilities[key].filter((l) => l.id !== liabilityId)
          })
          return {
            liabilities: newLiabilities,
            wizardState: { ...state.wizardState, hasUnsavedChanges: true },
          }
        }),

      // ==========================================
      // Safety Net Actions
      // ==========================================
      updateSafetyNet: (safetyNet) =>
        set((state) => ({
          safetyNet: { ...state.safetyNet, ...safetyNet },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // Savings & Goals Actions
      // ==========================================
      updateSavings: (savings) =>
        set((state) => ({
          savings: { ...state.savings, ...savings },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      addGoal: (goal) =>
        set((state) => ({
          savings: {
            ...state.savings,
            goals: [...state.savings.goals, goal],
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      updateGoal: (goalId, goalUpdate) =>
        set((state) => ({
          savings: {
            ...state.savings,
            goals: state.savings.goals.map((goal) =>
              goal.id === goalId ? { ...goal, ...goalUpdate } : goal
            ),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      removeGoal: (goalId) =>
        set((state) => ({
          savings: {
            ...state.savings,
            goals: state.savings.goals.filter((goal) => goal.id !== goalId),
          },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // Assumptions Actions
      // ==========================================
      updateAssumptions: (assumptions) =>
        set((state) => ({
          assumptions: { ...state.assumptions, ...assumptions },
          wizardState: { ...state.wizardState, hasUnsavedChanges: true },
        })),

      // ==========================================
      // Wizard Actions
      // ==========================================
      setCurrentStep: (step) =>
        set((state) => ({
          wizardState: { ...state.wizardState, currentStep: step },
        })),

      markStepCompleted: (step) =>
        set((state) => ({
          wizardState: {
            ...state.wizardState,
            completedSteps: state.wizardState.completedSteps.includes(step)
              ? state.wizardState.completedSteps
              : [...state.wizardState.completedSteps, step],
          },
        })),

      goToNextStep: () =>
        set((state) => {
          const nextStep = Math.min(state.wizardState.currentStep + 1, 6) as WizardStep
          return {
            wizardState: {
              ...state.wizardState,
              currentStep: nextStep,
              completedSteps: state.wizardState.completedSteps.includes(state.wizardState.currentStep)
                ? state.wizardState.completedSteps
                : [...state.wizardState.completedSteps, state.wizardState.currentStep],
            },
          }
        }),

      goToPreviousStep: () =>
        set((state) => ({
          wizardState: {
            ...state.wizardState,
            currentStep: Math.max(state.wizardState.currentStep - 1, 1) as WizardStep,
          },
        })),

      setCalculating: (isCalculating) =>
        set((state) => ({
          wizardState: { ...state.wizardState, isCalculating },
        })),

      setHasUnsavedChanges: (hasChanges) =>
        set((state) => ({
          wizardState: { ...state.wizardState, hasUnsavedChanges: hasChanges },
        })),

      // ==========================================
      // Results Actions
      // ==========================================
      setFIREResults: (results) => set({ fireResults: results }),
      setWorkDurationAnalysis: (analysis) => set({ workDurationAnalysis: analysis }),
      setProjections: (projections) => set({ projections }),
      setMonteCarloAnalysis: (analysis) => set({ monteCarloAnalysis: analysis }),
      setTaxAnalysis: (analysis) => set({ taxAnalysis: analysis }),
      setRecommendations: (recommendations) => set({ recommendations }),
      setComplianceItems: (items) => set({ complianceItems: items }),
      setRNORResult: (result) => set({ rnorResult: result }),

      // ==========================================
      // Utility Actions
      // ==========================================
      resetToDefaults: () => set(getDefaultState()),

      exportData: () => {
        const state = get()
        const exportableData = {
          profile: state.profile,
          location: state.location,
          incomeExpenses: state.incomeExpenses,
          residentCountryAssets: state.residentCountryAssets,
          archivedAssets: state.archivedAssets,
          indiaAssets: state.indiaAssets,
          liabilities: state.liabilities,
          safetyNet: state.safetyNet,
          savings: state.savings,
          assumptions: state.assumptions,
          wizardState: state.wizardState,
          exportedAt: new Date().toISOString(),
          version: '1.0',
        }
        return JSON.stringify(exportableData, null, 2)
      },

      importData: (jsonData: string) => {
        try {
          const data = JSON.parse(jsonData) as Partial<CalculatorState>
          set((state) => ({
            profile: data.profile ?? state.profile,
            location: data.location ?? state.location,
            incomeExpenses: data.incomeExpenses ?? state.incomeExpenses,
            residentCountryAssets: data.residentCountryAssets ?? state.residentCountryAssets,
            archivedAssets: data.archivedAssets ?? state.archivedAssets,
            indiaAssets: data.indiaAssets ?? state.indiaAssets,
            liabilities: data.liabilities ?? state.liabilities,
            safetyNet: data.safetyNet ?? state.safetyNet,
            savings: data.savings ?? state.savings,
            assumptions: data.assumptions ?? state.assumptions,
            wizardState: data.wizardState
              ? { ...data.wizardState, hasUnsavedChanges: false }
              : state.wizardState,
          }))
          return true
        } catch {
          return false
        }
      },

      updateLastSaved: () =>
        set((state) => ({
          wizardState: {
            ...state.wizardState,
            lastSavedAt: new Date(),
            hasUnsavedChanges: false,
          },
        })),

      // Dev helper: Load test data and jump to dashboard
      // PRODUCTION SAFEGUARD: This is a no-op in production builds
      // Demo-optimized test data for video recording
      // Target: FIRE ~₹10-11 Cr, Tax Savings ~₹18L, RNOR Eligible
      loadTestData: () => {
        if (!import.meta.env.DEV) {
          console.warn('loadTestData is only available in development mode');
          return;
        }
        set(() => ({
          profile: {
            firstName: 'Rahul',
            currentAge: 38,
            retirementAge: 50,
            lifeExpectancy: 85,
            maritalStatus: 'married' as const,
            spouseWorking: true,
            dependentParents: 2,
            children: [],
            residentCountry: 'USA' as const,
            targetCountry: 'India' as const,
            exchangeRate: 83.5,
            useCustomExchangeRate: false,
            yearsWorkedInUSA: 11,
            // RNOR eligible: Left India 2015 (11 years ago)
            nriSinceYear: 2015,
            expectedReturnYear: 2026,
            expectedReturnMonth: 10, // October - mid FY
          },
          location: {
            primaryCityId: 'bangalore',
            comparisonCityIds: ['mumbai', 'pune'],
            lifestyle: 'comfortable' as const,
            lifestyleMultiplier: 1.0,
            housingOwnership: 'rental' as const,
            housingType: '3BHK' as const,
            customMiscExpense: 0,
            includeSchoolFees: false,
            schoolBoard: 'cbse' as const,
          },
          incomeExpenses: {
            annualIncomeUSD: 180000,
            annualRSUIncomeUSD: 50000,
            annualIncomeGrowthPercent: 3,
            monthlyExpensesUSD: 6000,
            expectedExpenseReductionPercent: 25,
            monthlySavingsUSD: 12000,
            savingsRatePercent: 52,
            expectedIndiaSalaryINR: undefined,
            expectedIndiaSavingsRatePercent: undefined,
          },
          residentCountryAssets: {
            countryCode: 'USA' as const,
            assets: {
              savingsChecking: 30000,
              balance401k: 300000,
              balanceTraditionalIRA: 40000,
              balanceRothIRA: 60000,
              taxableBrokerage: 120000,
              balanceHSA: 15000,
              crypto: 0,
              esppBalance: 35000,
            },
            complexAssets: {
              rsuGrants: [
                {
                  id: 'rsu-1',
                  companyName: 'Tech Corp',
                  totalShares: 500,
                  vestedShares: 300,
                  unvestedShares: 200,
                  currentPricePerShare: 120,
                  vestingSchedule: [
                    { date: '2026-03-15', shares: 50 },
                    { date: '2026-06-15', shares: 50 },
                    { date: '2026-09-15', shares: 50 },
                    { date: '2026-12-15', shares: 50 },
                  ],
                },
              ],
              stockOptions: [],
              otherAssets: [],
            },
            settings: {
              stateTaxTier: 'high' as const,
            },
            lastUpdated: new Date(),
          },
          archivedAssets: [],
          indiaAssets: {
            npsBalance: 300000,
            epfBalance: 0,
            ppfBalance: 200000,
            mutualFunds: 1500000,
            stocks: 500000,
            fixedDeposits: 500000,
            gold: 0,
            realEstateValue: 0,
            realEstateRentalIncome: 0,
            nreAccountBalance: 0,
            nroAccountBalance: 0,
            otherAssets: [],
          },
          liabilities: {
            USA: [],
            UAE: [],
            India: [],
          },
          safetyNet: {
            termInsuranceCoverINR: 20000000,
            healthInsuranceCoverINR: 1000000,
            medicalBufferINR: 500000,
            emergencyFundMonths: 6,
            currentEmergencyFundUSD: 30000,
            currentEmergencyFundINR: 0,
          },
          savings: {
            monthlySIPUSD: 3000,
            monthlySIPINR: 0,
            annualStepUpPercent: 10,
            goals: [
              {
                id: 'goal-1',
                name: 'Parents Care',
                type: 'parentsCare' as const,
                frequency: 'annual' as const,
                costTodayValue: 500000, // ₹5L per year for parents care
                yearsFromNow: 0, // Starts immediately (pre & post retirement)
                durationYears: 25, // 25 years of care (13 post-retirement years affect FIRE)
                priority: 'essential' as const,
              },
              {
                id: 'goal-2',
                name: 'World Travel',
                type: 'worldTravel' as const,
                frequency: 'one-time' as const,
                costTodayValue: 2000000, // ₹20L world trip
                yearsFromNow: 14, // 2 years after retirement (post-retirement goal - affects FIRE!)
                priority: 'important' as const,
              },
            ],
            riskTolerance: 'moderate' as const,
            investmentExperience: 'intermediate' as const,
          },
          assumptions: {
            generalInflation: 6,
            medicalInflation: 10,
            educationInflation: 8,
            preRetirementReturn: 10,
            postRetirementReturn: 7,
            safeWithdrawalRate: 4,
            taxRegime: 'new' as const,
            expectedPensionAnnual: 0,
            expectedRentalMonthly: 0,
            currencyDepreciation: 2,
          },
          wizardState: {
            currentStep: 6,
            completedSteps: [1, 2, 3, 4, 5] as (1 | 2 | 3 | 4 | 5 | 6)[],
            isCalculating: false,
            hasUnsavedChanges: false,
          },
        }));
      },
    }),
    {
      name: 'nri-fire-calculator-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        profile: state.profile,
        location: state.location,
        incomeExpenses: state.incomeExpenses,
        residentCountryAssets: state.residentCountryAssets,
        archivedAssets: state.archivedAssets,
        indiaAssets: state.indiaAssets,
        liabilities: state.liabilities,
        safetyNet: state.safetyNet,
        savings: state.savings,
        assumptions: state.assumptions,
        wizardState: {
          ...state.wizardState,
          isCalculating: false,
        },
      }),
      version: 1,
    }
  )
)

// ==========================================
// Selectors
// ==========================================

export const selectCurrentStep = (state: AppStore) => state.wizardState.currentStep
export const selectCompletedSteps = (state: AppStore) => state.wizardState.completedSteps
export const selectIsCalculating = (state: AppStore) => state.wizardState.isCalculating
export const selectHasUnsavedChanges = (state: AppStore) => state.wizardState.hasUnsavedChanges
export const selectFIREResults = (state: AppStore) => state.fireResults
export const selectProfile = (state: AppStore) => state.profile
export const selectExchangeRate = (state: AppStore) => state.profile.exchangeRate
export const selectLocation = (state: AppStore) => state.location
export const selectAssumptions = (state: AppStore) => state.assumptions

/**
 * Get exchange rates for currency conversion
 *
 * IMPORTANT: profile.exchangeRate is LocalCurrency→INR (whatever the user's country is)
 * - For USA users: USD→INR (e.g., 83)
 * - For UAE users: AED→INR (e.g., 22.7)
 *
 * This helper provides both rates needed for conversions.
 */
function getExchangeRates(state: AppStore) {
  const config = getCountryConfig(state.residentCountryAssets.countryCode)
  const localCurrency = config.currency.code

  // profile.exchangeRate is LocalCurrency→INR (set by useExchangeRate hook)
  // Fallback to country config's defaultExchangeRate if not set
  const localToInr = state.profile.exchangeRate || config.currency.defaultExchangeRate

  // For USD→INR rate:
  // - If user is in USA, localToInr IS the USD→INR rate
  // - If user is NOT in USA, use USA config's defaultExchangeRate as fallback
  const usaConfig = getCountryConfig('USA')
  const usdToInr = localCurrency === 'USD'
    ? localToInr
    : usaConfig.currency.defaultExchangeRate

  return { localToInr, usdToInr, localCurrency }
}

/**
 * Convert USD to local currency
 */
function convertUSDToLocal(usdAmount: number, usdToInr: number, localToInr: number, localCurrency: string): number {
  if (localCurrency === 'USD') return usdAmount
  // USD → INR → Local = USD * (USD→INR) / (Local→INR)
  return usdAmount * usdToInr / localToInr
}

/**
 * Convert local currency to INR (direct conversion)
 */
function convertLocalToINR(localAmount: number, localToInr: number): number {
  return localAmount * localToInr
}

/**
 * Calculate total resident country assets in local currency
 * Works for any country (USA, UAE, etc.) using residentCountryAssets
 */
export const selectTotalResidentAssetsInLocalCurrency = (state: AppStore): number => {
  const { residentCountryAssets } = state
  const config = getCountryConfig(residentCountryAssets.countryCode)
  const { localToInr, usdToInr, localCurrency } = getExchangeRates(state)

  // Use country config's asset strategy to calculate net worth in local currency
  const totalFromAssets = config.assetStrategy.calculateNetWorth(
    residentCountryAssets.assets,
    config
  )

  // Add complex assets (RSUs, options, other)
  const complexAssets = residentCountryAssets.complexAssets || {}

  // RSUs and stock options are always priced in USD (company stock prices)
  const rsuValueUSD = (complexAssets.rsuGrants || []).reduce(
    (sum: number, grant: RSUGrant) => sum + grant.vestedShares * grant.currentPricePerShare,
    0
  )
  const optionsValueUSD = (complexAssets.stockOptions || []).reduce((sum: number, option: StockOption) => {
    const intrinsicValue = Math.max(0, option.currentPrice - option.strikePrice) * option.vestedShares
    return sum + intrinsicValue
  }, 0)

  // Convert RSU/options from USD to local currency
  const rsuValueLocal = convertUSDToLocal(rsuValueUSD, usdToInr, localToInr, localCurrency)
  const optionsValueLocal = convertUSDToLocal(optionsValueUSD, usdToInr, localToInr, localCurrency)

  // Other assets - convert based on their currency
  const otherValue = (complexAssets.otherAssets || []).reduce(
    (sum: number, asset: OtherAsset) => {
      // Same currency - no conversion
      if (asset.currency === localCurrency) {
        return sum + asset.value
      }
      // USD to local currency
      if (asset.currency === 'USD') {
        return sum + convertUSDToLocal(asset.value, usdToInr, localToInr, localCurrency)
      }
      // INR to local currency: INR / localToInr
      if (asset.currency === 'INR') {
        return sum + asset.value / localToInr
      }
      // AED to local currency (if local is not AED)
      if (asset.currency === 'AED' && localCurrency !== 'AED') {
        const uaeConfig = getCountryConfig('UAE')
        const aedToInr = uaeConfig.currency.defaultExchangeRate
        return sum + (asset.value * aedToInr) / localToInr
      }
      // Default: assume same currency
      return sum + asset.value
    },
    0
  )

  return totalFromAssets + rsuValueLocal + optionsValueLocal + otherValue
}

/**
 * Calculate total resident country assets in INR (direct conversion)
 * This is the PRIMARY selector - no USD intermediate needed
 */
export const selectTotalResidentAssetsINR = (state: AppStore): number => {
  const totalLocal = selectTotalResidentAssetsInLocalCurrency(state)
  const { localToInr } = getExchangeRates(state)
  return convertLocalToINR(totalLocal, localToInr)
}

/**
 * @deprecated Use selectTotalResidentAssetsINR instead
 * Calculate total resident country assets in USD (kept for backward compatibility)
 */
export const selectTotalResidentAssetsUSD = (state: AppStore): number => {
  const { localToInr, usdToInr, localCurrency } = getExchangeRates(state)
  const totalLocal = selectTotalResidentAssetsInLocalCurrency(state)

  // If already in USD, return as-is
  if (localCurrency === 'USD') {
    return totalLocal
  }

  // Local → INR → USD
  // Example (UAE): 100,000 AED * 22.7 (AED→INR) / 90 (USD→INR) = 25,222 USD
  return (totalLocal * localToInr) / usdToInr
}

/**
 * Calculate total India assets in INR
 */
export const selectTotalIndiaAssetsINR = (state: AppStore): number => {
  const { indiaAssets } = state
  const { usdToInr } = getExchangeRates(state)
  const uaeConfig = getCountryConfig('UAE')
  const aedToInr = uaeConfig.currency.defaultExchangeRate

  const retirement = indiaAssets.npsBalance + indiaAssets.epfBalance + indiaAssets.ppfBalance
  const investments = indiaAssets.mutualFunds + indiaAssets.stocks + indiaAssets.fixedDeposits
  const alternatives = indiaAssets.gold
  const realEstate = indiaAssets.realEstateValue
  const cash = indiaAssets.nreAccountBalance + indiaAssets.nroAccountBalance
  const other = indiaAssets.otherAssets.reduce(
    (sum, asset) => {
      if (asset.currency === 'INR') return sum + asset.value
      if (asset.currency === 'USD') return sum + asset.value * usdToInr
      if (asset.currency === 'AED') return sum + asset.value * aedToInr
      return sum + asset.value
    },
    0
  )

  return retirement + investments + alternatives + realEstate + cash + other
}

/**
 * Calculate total liabilities in INR (PRIMARY - no USD intermediate)
 */
export const selectTotalLiabilitiesINR = (state: AppStore): number => {
  const { liabilities } = state
  const { usdToInr } = getExchangeRates(state)
  const uaeConfig = getCountryConfig('UAE')
  const aedToInr = uaeConfig.currency.defaultExchangeRate
  let totalINR = 0

  // Iterate over all countries in the liabilities Record
  Object.entries(liabilities).forEach(([_countryCode, countryLiabilities]) => {
    countryLiabilities.forEach((l) => {
      if (l.currency === 'INR') {
        totalINR += l.balance
      } else if (l.currency === 'USD') {
        totalINR += l.balance * usdToInr
      } else if (l.currency === 'AED') {
        totalINR += l.balance * aedToInr
      }
    })
  })

  return totalINR
}

/**
 * @deprecated Use selectTotalLiabilitiesINR instead
 * Calculate total liabilities in USD (kept for backward compatibility)
 */
export const selectTotalLiabilitiesUSD = (state: AppStore): number => {
  const { usdToInr } = getExchangeRates(state)
  const totalINR = selectTotalLiabilitiesINR(state)
  return totalINR / usdToInr
}

/**
 * Calculate total net worth in INR (PRIMARY - no USD intermediate)
 * Direct conversion: Local → INR for resident assets
 */
export const selectTotalNetWorthINR = (state: AppStore): number => {
  const residentAssetsINR = selectTotalResidentAssetsINR(state)
  const indiaAssetsINR = selectTotalIndiaAssetsINR(state)
  const liabilitiesINR = selectTotalLiabilitiesINR(state)

  return residentAssetsINR + indiaAssetsINR - liabilitiesINR
}

/**
 * @deprecated Use selectTotalNetWorthINR instead
 * Calculate total net worth in USD (kept for backward compatibility)
 */
export const selectTotalNetWorthUSD = (state: AppStore): number => {
  const { usdToInr } = getExchangeRates(state)
  return selectTotalNetWorthINR(state) / usdToInr
}

/**
 * Calculate savings rate percentage (includes RSU income)
 */
export const selectSavingsRate = (state: AppStore): number => {
  const { annualIncomeUSD, annualRSUIncomeUSD, monthlyExpensesUSD } = state.incomeExpenses
  const totalIncome = annualIncomeUSD + (annualRSUIncomeUSD || 0)
  if (totalIncome <= 0) return 0
  const annualExpenses = monthlyExpensesUSD * 12
  const annualSavings = totalIncome - annualExpenses
  return (annualSavings / totalIncome) * 100
}

// ==========================================
// NEW: Country-Aware Selectors
// ==========================================

/**
 * Select current resident country code
 */
export const selectResidentCountryCode = (state: AppStore): CountryCode => {
  return state.residentCountryAssets.countryCode
}

/**
 * Select resident country assets
 */
export const selectResidentCountryAssets = (state: AppStore): Record<string, number> => {
  return state.residentCountryAssets.assets
}

/**
 * Select archived assets for all countries
 */
export const selectArchivedAssets = (state: AppStore): ArchivedAssets[] => {
  return state.archivedAssets
}

// ==========================================
// Country-Agnostic Liability Selectors
// ==========================================

/**
 * Get liabilities for resident country only
 */
export const selectResidentLiabilities = (state: AppStore): Liability[] => {
  const countryCode = state.residentCountryAssets.countryCode
  return state.liabilities[countryCode] || []
}

/**
 * Get liabilities for India (target country)
 */
export const selectIndiaLiabilities = (state: AppStore): Liability[] => {
  return state.liabilities['India'] || []
}

/**
 * Calculate total liabilities for a specific country in local currency
 */
export const selectCountryLiabilityTotal = (
  state: AppStore,
  countryCode: string
): number => {
  const liabilities = state.liabilities[countryCode as keyof typeof state.liabilities] || []
  return liabilities.reduce((sum, l) => sum + l.balance, 0)
}

/**
 * Calculate total liabilities for resident country in local currency
 */
export const selectResidentLiabilityTotal = (state: AppStore): number => {
  const countryCode = state.residentCountryAssets.countryCode
  return selectCountryLiabilityTotal(state, countryCode)
}

/**
 * Calculate total India liabilities in INR
 */
export const selectIndiaLiabilityTotalINR = (state: AppStore): number => {
  return selectCountryLiabilityTotal(state, 'India')
}

/**
 * Alias for selectTotalIndiaAssetsINR for consistency
 */
export const selectIndiaAssetTotal = selectTotalIndiaAssetsINR
